/* eslint import/no-default-export: off */
import Link from "next/link";
import { formatDate } from "@/lib/formatDate.js";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { siteConfig } from "../config/siteConfig";
import { Disclosure, Transition } from "@headlessui/react";
import Head from "next/head";

function collectHeadings(nodes) {
  const sections = [];
  Array.from(nodes).forEach((node) => {
    const { id, innerText: title, tagName: level } = node;
    if (!(id && title)) {
      return;
    }
    if (level === "H3") {
      const parentSection = sections[sections.length - 1];
      if (parentSection) parentSection.children.push({ id, title });
    } else if (level === "H2") {
      sections.push({ id, title, children: [] });
    }
    sections.push(...collectHeadings(node.children ?? []));
  });
  return sections;
}


function useTableOfContents(tableOfContents) {
  const [currentSection, setCurrentSection] = useState(tableOfContents[0]?.id);

  const getHeadings = useCallback((toc) => {
    return toc
      .flatMap((node) => [node.id, ...node.children.map((child) => child.id)])
      .map((id) => {
        const el = document.getElementById(id);
        if (!el) return null;
        const style = window.getComputedStyle(el);
        const scrollMt = parseFloat(style.scrollMarginTop);
        const top = window.scrollY + el.getBoundingClientRect().top - scrollMt;
        return { id, top };
      })
      .filter((el) => !!el);
  }, []);

  useEffect(() => {
    if (tableOfContents.length === 0) return;
    const headings = getHeadings(tableOfContents);
    function onScroll() {
      const top = window.scrollY + 4.5;
      let current = headings[0].id;
      headings.forEach((heading) => {
        if (top >= heading.top) {
          current = heading.id;
        }
        return current;
      });
      setCurrentSection(current);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll, { passive: true });
    };
  }, [getHeadings, tableOfContents]);

  return currentSection;
}

export function Button() {
  const initialText = 'Copy Link'
  const share = useRouter()
  const base = "https://www.datopian.com"
  const links = base + share.asPath
  const [buttonText, setButtonText] = useState(initialText)
  function handleClick() {
    setButtonText('Link copied!');
    setTimeout(() => {
      setButtonText(initialText);
    }, 10000); // 👈️ change text back after 1 second
  }
  const copylink = (e) => {
    navigator.clipboard.writeText(links)
  }
  return (
    <button onClick={(e) => { copylink(); handleClick() }} className="group caret-transparent hover:bg-orange-400 hover:cursor-pointer text-xs text-secgray hover:text-white font-semibold w-[9.5rem] h-8 border rounded-lg flex gap-1 items-center justify-center ease-in-out transition">
      <svg width="20" height="16" viewBox="0 0 20 16" className="fill-[#556987] group-hover:fill-white" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 13.8333H4.99998C4.33694 13.8333 3.70105 13.5699 3.23221 13.1011C2.76337 12.6323 2.49998 11.9964 2.49998 11.3333V4.66667C2.49998 4.44565 2.41218 4.23369 2.2559 4.07741C2.09962 3.92113 1.88766 3.83333 1.66665 3.83333C1.44563 3.83333 1.23367 3.92113 1.07739 4.07741C0.92111 4.23369 0.833313 4.44565 0.833313 4.66667V11.3333C0.833313 12.4384 1.2723 13.4982 2.0537 14.2796C2.44061 14.6665 2.89994 14.9734 3.40546 15.1828C3.91099 15.3922 4.4528 15.5 4.99998 15.5H15C15.221 15.5 15.433 15.4122 15.5892 15.2559C15.7455 15.0996 15.8333 14.8877 15.8333 14.6667C15.8333 14.4457 15.7455 14.2337 15.5892 14.0774C15.433 13.9211 15.221 13.8333 15 13.8333ZM19.1666 6.28333C19.158 6.20678 19.1412 6.13136 19.1166 6.05833V5.98333C19.0766 5.89765 19.0231 5.81889 18.9583 5.75L13.9583 0.75C13.8894 0.68518 13.8107 0.631734 13.725 0.591667H13.65L13.3833 0.5H6.66665C6.0036 0.5 5.36772 0.763392 4.89888 1.23223C4.43004 1.70107 4.16665 2.33696 4.16665 3V9.66667C4.16665 10.3297 4.43004 10.9656 4.89888 11.4344C5.36772 11.9033 6.0036 12.1667 6.66665 12.1667H16.6666C17.3297 12.1667 17.9656 11.9033 18.4344 11.4344C18.9033 10.9656 19.1666 10.3297 19.1666 9.66667V6.33333C19.1666 6.33333 19.1666 6.33333 19.1666 6.28333ZM14.1666 3.34167L16.325 5.5H15C14.779 5.5 14.567 5.4122 14.4107 5.25592C14.2544 5.09964 14.1666 4.88768 14.1666 4.66667V3.34167ZM17.5 9.66667C17.5 9.88768 17.4122 10.0996 17.2559 10.2559C17.0996 10.4122 16.8877 10.5 16.6666 10.5H6.66665C6.44563 10.5 6.23367 10.4122 6.07739 10.2559C5.92111 10.0996 5.83331 9.88768 5.83331 9.66667V3C5.83331 2.77899 5.92111 2.56702 6.07739 2.41074C6.23367 2.25446 6.44563 2.16667 6.66665 2.16667H12.5V4.66667C12.5 5.32971 12.7634 5.96559 13.2322 6.43443C13.7011 6.90327 14.3369 7.16667 15 7.16667H17.5V9.66667Z" />
      </svg>{buttonText}
    </button>
  )
}

export default function PlaybookLayout({ children, ...frontMatter }) {
  const { title, seotitle, created, image } = frontMatter;
  const share = useRouter()
  const base = "https://www.datopian.com"
  const links = base + share.asPath
  const router = useRouter();
  const [tableOfContents, setTableOfContents] = useState([]);
  const currentSection = useTableOfContents(tableOfContents);
  useEffect(() => {
  }, [router.events]);
  useEffect(() => {
    const headingNodes = document.querySelectorAll("h2,h3");
    const toc = collectHeadings(headingNodes);
    setTableOfContents(toc ?? []);
  }, [router.asPath])

  function isActiveSection(section) {
    if (section.id === currentSection) {
      return true;
    }
    if (!section.children) {

      return false;
    }
    return section.children.findIndex(isActiveSection) > -1;
  }

  function useScrollDirection() {
    const [scrollDirection, setScrollDirection] = useState(null);

    useEffect(() => {
      let lastScrollY = window.pageYOffset;

      const updateScrollDirection = () => {
        const scrollY = window.pageYOffset;
        const direction = scrollY > lastScrollY ? "down" : "up";
        if (direction !== scrollDirection && (scrollY - lastScrollY > 10 || scrollY - lastScrollY < -10)) {
          setScrollDirection(direction);
        }
        lastScrollY = scrollY > 0 ? scrollY : 0;
      };
      window.addEventListener("scroll", updateScrollDirection); // add event listener
      return () => {
        window.removeEventListener("scroll", updateScrollDirection); // clean up
      }
    }, [scrollDirection]);

    return scrollDirection;
  };

  const scrollDirection = useScrollDirection();

  const [isVisible, setIsVisible] = useState(true);
  const [height, setHeight] = useState(0)

  useEffect(() => {
    window.addEventListener("scroll", listenToScroll);
    return () =>
      window.removeEventListener("scroll", listenToScroll);
  }, [])

  const listenToScroll = () => {
    let heightToHideFrom = document.documentElement.scrollHeight - 1750;
    const winScroll = document.body.scrollTop ||
      document.documentElement.scrollTop;
    setHeight(winScroll);

    if (winScroll > heightToHideFrom) {
      isVisible && setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  };


  return (
    <article className="font-['Inter'] lg:max-w-5xl xl:max-w-6xl prose docs dark:prose-invert prose-a:break-words mx-auto px-4 sm:px-8 ">
      <header className="flex justify-center gap-8">
      <Head><title>{seotitle ? seotitle : title}</title> </Head>
        <div className="flex justify-center pt-20">
          {title && (
            <h1 className="main-top-title">{title}</h1>
          )}
        </div>
        
      </header>
      <div className="flex justify-end gap-8 ">
        {isVisible &&
          <div className={`sticky ${scrollDirection === "down" ? "top-16" : "top-16"} h-32 hidden lg:block w-1/4`}>
            <ol className="mt-6 space-y-2 text-sm list-outside list-none text-black caret-transparent">
              {tableOfContents.map((section) => (
                <li className="text-black" key={section.id}>
                  <Disclosure defaultOpen>
                    {({ open }) => (<>                    <div className="text-black">
                      <a
                        href={`#${section.id}`}
                        className={
                          isActiveSection(section) ? "no-underline text-secondary text-xs" : "no-underline text-secgray text-xs"}
                      >
                        {section.title}
                      </a>
                    </div>
                      {section.children && section.children.length > 0 && (
                        isActiveSection(section) &&
                        <Disclosure.Panel>
                          <ol className="space-y-2 list-none list-outside">
                            {section.children.map((subSection) => (

                              <li key={subSection.id}>

                                <a
                                  href={`#${subSection.id}`}
                                  className={
                                    isActiveSection(subSection) ? "no-underline text-secondary text-xs" : "no-underline text-secgray text-xs"}
                                >
                                  {subSection.title}
                                </a>

                              </li>
                            ))}
                          </ol>
                        </Disclosure.Panel>
                      )}</>)}

                  </Disclosure>
                </li>
              ))}
            </ol>
            <div className="max-w-sm border-t pt-4 border-1 flex flex-col pl-5  gap-3">
              <Button />
              <div className="flex gap-2">
                <a href={`https://www.twitter.com/intent/tweet?text=${links}`} target="_blank">
                  <div className="hover:bg-orange-400 hover:cursor-pointer ease-in-out transition w-8 h-8 border border-1 rounded-lg flex items-center justify-center group">
                    <svg width="18" height="14" viewBox="0 0 18 14" className="fill-[#556987] group-hover:fill-white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.3334 1.83332C16.707 2.10507 16.0445 2.28471 15.3667 2.36666C16.0818 1.93943 16.6177 1.26731 16.875 0.474992C16.203 0.875044 15.4673 1.15691 14.7 1.30832C14.1871 0.752135 13.5042 0.381905 12.7582 0.255701C12.0122 0.129497 11.2455 0.254457 10.5782 0.610983C9.91089 0.967509 9.3808 1.53543 9.07105 2.22569C8.76131 2.91594 8.68943 3.68948 8.86669 4.42499C7.50788 4.35626 6.17872 4.00245 4.96557 3.38654C3.75241 2.77063 2.68239 1.9064 1.82502 0.849991C1.5243 1.37513 1.36629 1.96984 1.36669 2.57499C1.36562 3.13698 1.50353 3.69051 1.76815 4.1863C2.03276 4.68208 2.41587 5.10475 2.88335 5.41666C2.34 5.40187 1.80826 5.25607 1.33335 4.99166V5.03332C1.33743 5.82073 1.61335 6.58257 2.11445 7.18996C2.61555 7.79736 3.31107 8.21303 4.08335 8.36666C3.78607 8.45713 3.47742 8.50483 3.16669 8.50833C2.9516 8.50581 2.73704 8.48631 2.52502 8.44999C2.74495 9.12733 3.17054 9.71928 3.74258 10.1435C4.31463 10.5676 5.00467 10.803 5.71669 10.8167C4.51436 11.7627 3.02992 12.279 1.50002 12.2833C1.22147 12.2842 0.943133 12.2675 0.666687 12.2333C2.22871 13.2419 4.04903 13.7773 5.90835 13.775C7.19143 13.7883 8.4643 13.5458 9.65262 13.0617C10.8409 12.5776 11.9209 11.8616 12.8294 10.9554C13.7378 10.0492 14.4567 8.97115 14.9438 7.78408C15.431 6.59701 15.6767 5.32476 15.6667 4.04166C15.6667 3.89999 15.6667 3.74999 15.6667 3.59999C16.3206 3.11233 16.8846 2.51451 17.3334 1.83332V1.83332Z" />
                    </svg>
                  </div>
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${links}`} target="_blank">
                  <div className="group hover:bg-orange-400 hover:cursor-pointer ease-in-out transition w-8 h-8 border border-1 rounded-lg flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 14 14" className="fill-[#556987] group-hover:fill-white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.25886 4.50394H0.448341C0.323605 4.50394 0.222534 4.60505 0.222534 4.72974V13.7587C0.222534 13.8835 0.323605 13.9846 0.448341 13.9846H3.25886C3.3836 13.9846 3.48467 13.8835 3.48467 13.7587V4.72974C3.48467 4.60505 3.3836 4.50394 3.25886 4.50394Z" />
                      <path d="M1.85459 0.0154114C0.831961 0.0154114 0 0.846469 0 1.86797C0 2.88993 0.831961 3.7213 1.85459 3.7213C2.87641 3.7213 3.7077 2.88988 3.7077 1.86797C3.70774 0.846469 2.87641 0.0154114 1.85459 0.0154114Z" />
                      <path d="M10.4076 4.27954C9.27879 4.27954 8.44435 4.7648 7.93822 5.31617V4.72975C7.93822 4.60506 7.83715 4.50395 7.71242 4.50395H5.02085C4.89611 4.50395 4.79504 4.60506 4.79504 4.72975V13.7588C4.79504 13.8835 4.89611 13.9846 5.02085 13.9846H7.82523C7.94997 13.9846 8.05104 13.8835 8.05104 13.7588V9.2915C8.05104 7.78613 8.45993 7.19967 9.5093 7.19967C10.6521 7.19967 10.743 8.13984 10.743 9.36895V13.7588C10.743 13.8835 10.844 13.9846 10.9688 13.9846H13.7742C13.8989 13.9846 14 13.8835 14 13.7588V8.80624C14 6.56782 13.5732 4.27954 10.4076 4.27954Z" />
                    </svg>
                  </div>
                </a>
                <a href={`https://www.facebook.com/dialog/share?app_id=145634995501895&display=popup&href=${links}`} target="_blank">
                  <div className="group hover:bg-orange-400 hover:cursor-pointer ease-in-out transition w-8 h-8 border border-1 rounded-lg flex items-center justify-center">
                    <svg width="10" height="18" viewBox="0 0 10 18" className="fill-[#556987] group-hover:fill-white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7.60003 3.43335H9.16669V0.783348C8.40815 0.704471 7.64599 0.665528 6.88336 0.666681C4.61669 0.666681 3.0667 2.05001 3.0667 4.58335V6.76668H0.508362V9.73335H3.0667V17.3333H6.13336V9.73335H8.68336L9.06669 6.76668H6.13336V4.87502C6.13336 4.00002 6.3667 3.43335 7.60003 3.43335Z" />
                    </svg>
                  </div>
                </a>
                <a href={`https://www.mastodon.social/share?text=${links}`} target="_blank">
                  <div className="group hover:bg-orange-400 hover:cursor-pointer ease-in-out transition w-8 h-8 border border-1 rounded-lg flex items-center justify-center">
                    <svg width="16" height="18" viewBox="0 0 16 18" className="fill-[#556987] group-hover:fill-white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.9994 5.90951C15.9994 2.00434 13.5612 0.860417 13.5612 0.860417C12.3322 0.268205 10.2217 0.0189004 8.02875 0H7.97472C5.78181 0.0189004 3.67294 0.268205 2.44313 0.860417C2.44313 0.860417 0.00494219 2.00524 0.00494219 5.90951C0.00494219 6.80323 -0.0113525 7.87245 0.0152334 9.00647C0.103567 12.8252 0.682454 16.5891 4.04686 17.5233C5.59828 17.9544 6.93015 18.0444 8.00216 17.9823C9.94722 17.8689 11.039 17.2542 11.039 17.2542L10.9746 15.7728C10.9746 15.7728 9.58445 16.2327 8.0236 16.1769C6.47647 16.1211 4.84444 16.0023 4.59402 14.0088C4.57037 13.8235 4.55891 13.6368 4.55971 13.4499C4.55971 13.4499 6.07768 13.8396 8.00216 13.9323C9.1788 13.989 10.2817 13.8603 11.4026 13.7199C13.5518 13.4508 15.4231 12.0611 15.658 10.7912C16.0302 8.79047 15.9994 5.90951 15.9994 5.90951ZM13.1238 10.9406H11.3391V6.35142C11.3391 5.3839 10.9515 4.89339 10.1753 4.89339C9.31773 4.89339 8.88807 5.47571 8.88807 6.62773V9.13968H7.11367V6.62773C7.11367 5.47571 6.68401 4.89339 5.82554 4.89339C5.04941 4.89339 4.66177 5.3848 4.66177 6.35142V10.9406H2.87708V6.21282C2.87708 5.2462 3.11121 4.47849 3.58289 3.91058C4.0683 3.34266 4.70379 3.05106 5.49365 3.05106C6.407 3.05106 7.0991 3.41917 7.5562 4.15628L8.00044 4.9384L8.44554 4.15628C8.90265 3.41917 9.59474 3.05106 10.5081 3.05106C11.2971 3.05106 11.9334 3.34266 12.4189 3.91058C12.8897 4.47849 13.1238 5.2462 13.1238 6.21282V10.9406Z" />
                    </svg>
                  </div>
                </a>
              </div>
            </div>
            <div className="pl-6 xl: pl-0">
              <div className="w-full flex flex-col items-center sm:items-start" >
                <h3 className="inline-flex sm:text-left small-title mb-2 mt-4 !text-secgray !text-sm">
                  Newsletter
                </h3>
                <p className="text-secgray text-xs">A monthly dose of Datopian&apos;s finest insights, delivered right to your inbox.</p>
                <Link href="/newsletter">
                  <button className="btn-primary !px-3 !py-2 !text-xs">
                    Subscribe
                  </button>
                </Link>
              </div>
            </div>
          </div>
        }
        <div className="gap-12 sticky overflow-hidden lg:w-3/4">
          <section className="px-4 md:px-0 max-w-4xl mx-auto break-words">{children}</section>
        </div>
      </div>
    </article >
  )
}
